<div class="py-4 manage-recipe-container">
  <div class="manage-recipe-layout flex flex-col lg:flex-row lg:items-start lg:gap-6">
    <div class="manage-recipe-main w-full lg:flex-1 lg:min-w-0">
      <!-- Toolbar actions (Save / Delete) are registered declaratively in the
           component via PageHeaderService.setPageActions(...). An effect()
           re-publishes them whenever isSaving / isDeleting / isEditMode change. -->

      <!-- Mobile Ingredients (shown only on small screens) -->
      <div class="manage-recipe-ingredients-mobile">
        <ui-card [shadow]="false" [bordered]="false">
          <div class="p-4">
            <div class="flex justify-between items-center mb-3">
              <h5 class="text-lg font-semibold">Ingredients</h5>
              <ui-button
                variant="primary"
                [outline]="true"
                size="sm"
                (clicked)="previewIngredients()"
              >
                <fa-icon [icon]="faSync"></fa-icon>
              </ui-button>
            </div>

            @if (aggregatedIngredients().length > 0) {
              <ul class="divide-y divide-base-300">
                @for (ingredient of aggregatedIngredients(); track ingredient.name) {
                  <li class="py-3 first:pt-0">
                    <div class="flex justify-between items-center">
                      <div class="flex items-center gap-2 grow min-w-0">
                        <span class="ingredient-name">{{ ingredient.name }}</span>
                      </div>

                      @if (ingredient.quantity?.readable) {
                        <ui-badge variant="primary" class="ml-2">
                          {{ ingredient.quantity!.readable }}
                        </ui-badge>
                      } @else {
                        <ui-badge variant="error" class="ml-2">?</ui-badge>
                      }
                    </div>
                  </li>
                }
              </ul>

              <div class="mt-3 text-base-content/60 text-sm text-center">
                <strong>{{ validIngredientsCount }}</strong> ingredient(s) found
              </div>
            } @else {
              <div class="empty-ingredients-state">
                <p class="text-base-content/70 mb-3 text-center">
                  <i>Mark quantities and ingredients in the steps.</i>
                </p>
                <ui-button
                  variant="secondary"
                  size="sm"
                  (clicked)="previewIngredients()"
                >
                  <fa-icon [icon]="faPlus"></fa-icon>
                  Generate ingredients
                </ui-button>
              </div>
            }
          </div>
        </ui-card>
      </div>

      <!-- Recipe Meta Information -->
      <div class="mb-4">
        <div class="p-4">
          <h5 class="text-lg font-semibold mb-3">Basic information</h5>

          <!-- Title - Full Width -->
          <div class="mb-3">
            <label for="recipeTitle" class="label">
              <span class="label-text">Recipe title <span class="text-error">*</span></span>
            </label>
            <ui-input
              [value]="recipeTitle()"
              placeholder="e.g. Homemade bratwurst"
              [fullWidth]="true"
              ariaLabel="Recipe title"
              (valueChange)="recipeTitle.set($event); formTouched() && validateForm()"
              (blur)="formTouched.set(true); validateForm()"
            ></ui-input>
            @if (formTouched() && validationErrors().title) {
              <div class="text-error text-sm mt-1">
                {{ validationErrors().title }}
              </div>
            }
          </div>

          <!-- Servings, Prep Time & Cook Time - Side by Side -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
            <div>
              <label for="servings" class="label"><span class="label-text">Servings</span></label>
              <ui-input
                type="number"
                [value]="'' + servings()"
                [fullWidth]="true"
                ariaLabel="Servings"
                (valueChange)="servings.set(+$event)"

                (blur)="formTouched.set(true); validateForm()"
              ></ui-input>
            </div>
            <div>
              <label for="prepTime" class="label"><span class="label-text">Prep time (min)</span></label>
              <ui-input
                type="number"
                [value]="'' + prepTime()"
                [fullWidth]="true"
                ariaLabel="Prep time"
                (valueChange)="prepTime.set(+$event)"
                (blur)="formTouched.set(true); validateForm()"
              ></ui-input>
            </div>
            <div>
              <label class="label"><span class="label-text">On the table in (min)</span></label>
              <ui-input
                type="number"
                [value]="'' + onTableTime()"
                [fullWidth]="true"
                [disabled]="true"
                ariaLabel="On the table in"
              ></ui-input>
              <p class="text-xs text-base-content/50 mt-1">Automatically calculated from prep and step times.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Steps -->
      <div class="mb-4">
        <div class="p-4">

          <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4 gap-1">
            <h5 class="text-lg font-semibold">
              Steps
            </h5>

            <p class="text-sm text-base-content/60 lg:text-right">
              Type an amount + unit (e.g. "300g") and press Space for automatic ingredient detection.
            </p>
          </div>

          <!-- Steps list -->
          <div class="steps-wrapper">
            @if (steps().length === 0) {
              <div class="text-center py-4 text-base-content/60">
                <p>No steps yet. Add a step above.</p>
              </div>
            }

            @for (step of steps(); track step.id) {
              <div
                class="step-container border border-base-300 rounded-lg mb-3 transition-colors duration-200 hover:shadow"
                [class.step-dragging]="isDragging(step.id)"
                [ngClass]="{
                  'step-type--instruction': step.type === 'instruction',
                  'step-type--cook': step.type === 'cook',
                  'step-type--wait': step.type === 'wait',
                  'step-type--serve': step.type === 'serve'
                }"
                (dragover)="onDragOver($event, step.id)"
                (drop)="onDrop($event)"
              >
                <!-- Drag Handle (Left Side) -->
                <div
                  class="step-drag-handle"
                  draggable="true"
                  (dragstart)="onDragStart($event, step.id)"
                  (dragend)="onDragEnd($event)"
                >
                  <fa-icon [icon]="faGripVertical"></fa-icon>
                </div>

                <!-- Step Type Icon (Top Right) -->
                <div
                  class="step-type-icon"
                  [ngClass]="{
                  'step-icon--instruction': step.type === 'instruction',
                  'step-icon--cook': step.type === 'cook',
                  'step-icon--wait': step.type === 'wait',
                  'step-icon--serve': step.type === 'serve'
                }"
                >
                  @if (step.type === 'instruction') {
                    <fa-icon [icon]="faBook"></fa-icon>
                  } @else if (step.type === 'cook') {
                    <fa-icon [icon]="faFire"></fa-icon>
                  } @else if (step.type === 'wait') {
                    <fa-icon [icon]="faClock"></fa-icon>
                  } @else if (step.type === 'serve') {
                    <fa-icon [icon]="faUtensils"></fa-icon>
                  }
                </div>

                <!-- Step Content -->
                <div
                  class="step-content"
                  (dragover)="onContentDragOver($event)"
                  (drop)="onContentDrop($event)"
                >
                  @if (step.type === 'instruction') {
                    <!-- Instruction Step with Editor -->
                    <app-recipe-editor-v2
                      [serializedText]="step.text || ''"
                      [autocompleteIngredients]="ingredientNames"
                      [emitOnBlur]="true"
                      (onBlur)="onEditorBlur()"
                      (serializedTextChange)="onStepTextChange(step.id, $event)">
                    </app-recipe-editor-v2>
                  } @else if (step.type === 'cook') {
                    <!-- Cook Step with Settings -->
                    <div class="cook-step-wrapper">
                      <div class="oven-step-controls">
                        @if (step.device === 'oven') {
                          <!-- Oven: Two-row layout with rack spanning both rows on the right -->
                          <div class="oven-controls-grid">
                            <div class="oven-controls-left">
                              <!-- Row 1: Device, Mode -->
                              <div class="cook-controls-row flex items-center gap-2 mb-2">
                                <label class="text-sm font-medium min-w-[60px]">Device</label>
                                <ui-select
                                  size="sm"
                                  [value]="step.device || ''"
                                  [options]="[
                                    { value: 'oven', label: 'Oven' },
                                    { value: 'pan', label: 'Pan' },
                                    { value: 'pot', label: 'Pot' },
                                    { value: 'grill', label: 'Grill' },
                                    { value: 'airfryer', label: 'Air fryer' }
                                  ]"
                                  (valueChange)="onDeviceChange(step.id, $event)"
                                ></ui-select>

                                <label class="text-sm font-medium min-w-[60px]">Mode</label>
                                <ui-select
                                  size="sm"
                                  [value]="step.mode || ''"
                                  [options]="[
                                    { value: 'top_bottom_heat', label: 'Top/bottom heat' },
                                    { value: 'convection', label: 'Convection' },
                                    { value: 'grill', label: 'Grill' }
                                  ]"
                                  (valueChange)="onModeChange(step.id, $event)"
                                ></ui-select>
                              </div>

                              <!-- Row 2: Temp, Time, Preheat -->
                              <div class="cook-controls-row flex items-center gap-2">
                                <label class="text-sm font-medium min-w-[60px]">Temp (°C)</label>
                                <div class="w-24">
                                  <ui-input
                                    type="number"
                                    size="sm"
                                    [fullWidth]="true"
                                    [value]="'' + (step.tempC ?? 0)"
                                    ariaLabel="Temperature"
                                    (valueChange)="onTempCChange(step.id, +$event)"
                                  ></ui-input>
                                </div>

                                <label class="text-sm font-medium min-w-[60px]">Time (min)</label>
                                <div class="w-24">
                                  <ui-input
                                    type="number"
                                    size="sm"
                                    [fullWidth]="true"
                                    [value]="'' + (step.timeMin ?? 0)"
                                    ariaLabel="Time"
                                    (valueChange)="onTimeMinChange(step.id, +$event)"
                                  ></ui-input>
                                </div>

                                <ui-checkbox
                                  [checked]="!!step.preheat"
                                  label="Preheat"
                                  (checkedChange)="onPreheatChange(step.id, $event)"
                                ></ui-checkbox>
                              </div>
                            </div>

                            <!-- Rack: Spans both rows on the right -->
                            <div class="oven-controls-right">
                              <div class="rack-container">
                                <label class="text-sm font-medium min-w-[40px]">Rack</label>
                                <div class="rack-position-selector">
                                  @for (position of [1, 2, 3, 4, 5]; track position) {
                                    <button
                                      type="button"
                                      class="rack-position-btn"
                                      [class.active]="step.rackPosition === position"
                                      (click)="onRackPositionChange(step.id, asRackPosition(position))"
                                      [title]="'Rack ' + position"
                                    >
                                    </button>
                                  }
                                </div>
                              </div>
                            </div>
                          </div>
                        } @else {
                          <!-- Non-oven devices: Single row on desktop, stacked on mobile -->
                          <div class="non-oven-controls flex items-center gap-2">
                            <label class="text-sm font-medium min-w-[60px]">Device</label>
                            <ui-select
                              size="sm"
                              [value]="step.device || ''"
                              [options]="[
                                { value: 'oven', label: 'Oven' },
                                { value: 'pan', label: 'Pan' },
                                { value: 'pot', label: 'Pot' },
                                { value: 'grill', label: 'Grill' },
                                { value: 'airfryer', label: 'Air fryer' }
                              ]"
                              (valueChange)="onDeviceChange(step.id, $event)"
                            ></ui-select>

                            @if (step.device === 'pan') {
                              <label class="text-sm font-medium min-w-[60px]">Time (min)</label>
                              <div class="w-24">
                                <ui-input
                                  type="number"
                                  size="sm"
                                  [fullWidth]="true"
                                  [value]="'' + (step.timeMin ?? 0)"
                                  ariaLabel="Time"
                                  (valueChange)="onTimeMinChange(step.id, +$event)"
                                ></ui-input>
                              </div>

                              <label class="text-sm font-medium min-w-[60px]">Level</label>
                              <div class="heat-level-selector">
                                @for (level of [1, 2, 3, 4, 5, 6, 7, 8, 9]; track level) {
                                  <button
                                    type="button"
                                    class="heat-level-btn"
                                    [class.active]="step.heatLevel === level"
                                    (click)="onHeatLevelChange(step.id, asHeatLevel(level))"
                                    [title]="'Level ' + level"
                                  >
                                    {{ level }}
                                  </button>
                                }
                              </div>
                            } @else if (step.device === 'grill') {
                              <label class="text-sm font-medium min-w-[60px]">Time (min)</label>
                              <div class="w-24">
                                <ui-input
                                  type="number"
                                  size="sm"
                                  [fullWidth]="true"
                                  [value]="'' + (step.timeMin ?? 0)"
                                  ariaLabel="Time"
                                  (valueChange)="onTimeMinChange(step.id, +$event)"
                                ></ui-input>
                              </div>

                              <label class="text-sm font-medium min-w-[60px]">Level</label>
                              <div class="heat-level-selector">
                                @for (level of [1, 2, 3, 4, 5, 6, 7, 8, 9]; track level) {
                                  <button
                                    type="button"
                                    class="heat-level-btn"
                                    [class.active]="step.heatLevel === level"
                                    (click)="onHeatLevelChange(step.id, asHeatLevel(level))"
                                    [title]="'Level ' + level"
                                  >
                                    {{ level }}
                                  </button>
                                }
                              </div>

                              <label class="text-sm font-medium min-w-[60px]">Heat</label>
                              <ui-select
                                size="sm"
                                [value]="step.grillMode || 'direct'"
                                [options]="[
                                  { value: 'direct', label: 'Direct' },
                                  { value: 'indirect', label: 'Indirect' }
                                ]"
                                (valueChange)="onGrillModeChange(step.id, $event)"
                              ></ui-select>
                            } @else {
                              <label class="text-sm font-medium min-w-[60px]">Temp (°C)</label>
                              <div class="w-24">
                                <ui-input
                                  type="number"
                                  size="sm"
                                  [fullWidth]="true"
                                  [value]="'' + (step.tempC ?? 0)"
                                  ariaLabel="Temperature"
                                  (valueChange)="onTempCChange(step.id, +$event)"
                                ></ui-input>
                              </div>

                              <label class="text-sm font-medium min-w-[60px]">Time (min)</label>
                              <div class="w-24">
                                <ui-input
                                  type="number"
                                  size="sm"
                                  [fullWidth]="true"
                                  [value]="'' + (step.timeMin ?? 0)"
                                  ariaLabel="Time"
                                  (valueChange)="onTimeMinChange(step.id, +$event)"
                                ></ui-input>
                              </div>

                              <ui-checkbox
                                [checked]="!!step.preheat"
                                label="Preheat"
                                (checkedChange)="onPreheatChange(step.id, $event)"
                              ></ui-checkbox>
                            }
                          </div>
                        }
                      </div>

                      <!-- Cook Step Text Editor or Add Button -->
                      <div class="cook-step-text mt-2">
                        @if (isCookStepTextEditorVisible(step.id, step.text)) {
                          <app-recipe-editor-v2
                            [serializedText]="step.text || ''"
                            [autocompleteIngredients]="ingredientNames"
                            [emitOnBlur]="true"
                            (onBlur)="onEditorBlur()"
                            (serializedTextChange)="onStepTextChange(step.id, $event)">
                          </app-recipe-editor-v2>
                        } @else {
                          <ui-button
                            variant="secondary"
                            [outline]="true"
                            size="sm"
                            (clicked)="showCookStepTextEditor(step.id)"
                          >
                            <fa-icon [icon]="faPlus"></fa-icon>
                            Add text
                          </ui-button>
                        }
                      </div>
                    </div>
                  } @else if (step.type === 'wait') {
                    <!-- Wait Step with Duration -->
                    <div class="wait-step-wrapper">
                      <div class="wait-step-controls">
                        <div class="flex items-center gap-2">
                          <label class="text-sm font-medium min-w-[60px]">Location</label>
                          <ui-select
                            size="sm"
                            [value]="step.waitLocation || ''"
                            [options]="[
                              { value: 'room', label: 'Room temperature' },
                              { value: 'fridge', label: 'Fridge' }
                            ]"
                            (valueChange)="onWaitLocationChange(step.id, $event)"
                          ></ui-select>

                          <label class="text-sm font-medium min-w-[60px]">Time</label>
                          <div class="w-24">
                            <ui-input
                              type="number"
                              size="sm"
                              [fullWidth]="true"
                              [value]="'' + (step.waitTimeMin ?? 0)"
                              ariaLabel="Wait time"
                              (valueChange)="onWaitTimeMinChange(step.id, +$event)"
                            ></ui-input>
                          </div>
                          <span class="text-sm text-base-content/60">min</span>

                          <ui-checkbox
                            [checked]="!!step.waitCovered"
                            label="Covered"
                            (checkedChange)="onWaitCoveredChange(step.id, $event)"
                          ></ui-checkbox>
                        </div>
                      </div>

                      <!-- Wait Step Text Editor or Add Button -->
                      <div class="wait-step-text mt-2">
                        @if (isWaitStepTextEditorVisible(step.id, step.text)) {
                          <app-recipe-editor-v2
                            [serializedText]="step.text || ''"
                            [autocompleteIngredients]="ingredientNames"
                            [emitOnBlur]="true"
                            (onBlur)="onEditorBlur()"
                            (serializedTextChange)="onStepTextChange(step.id, $event)">
                          </app-recipe-editor-v2>
                        } @else {
                          <ui-button
                            variant="secondary"
                            [outline]="true"
                            size="sm"
                            (clicked)="showWaitStepTextEditor(step.id)"
                          >
                            <fa-icon [icon]="faPlus"></fa-icon>
                            Add text
                          </ui-button>
                        }
                      </div>
                    </div>
                  } @else if (step.type === 'serve') {
                    <!-- Serve Step with Text Editor -->
                    <app-recipe-editor-v2
                      [serializedText]="step.text || ''"
                      [autocompleteIngredients]="ingredientNames"
                      [emitOnBlur]="true"
                      (onBlur)="onEditorBlur()"
                      (serializedTextChange)="onStepTextChange(step.id, $event)">
                    </app-recipe-editor-v2>
                  }
                </div>

                <!-- Delete Button (Bottom Right) -->
                <div class="mt-2 flex justify-end">
                  <ui-button
                    variant="danger"
                    [outline]="true"
                    size="sm"
                    [disabled]="steps().length <= 1"
                    (clicked)="removeStep(step.id)"
                  >
                    <fa-icon [icon]="faTrash"></fa-icon>
                  </ui-button>
                </div>
              </div>
            }

            <!-- Step type buttons -->
            <div class="flex gap-2">
              <ui-button
                variant="secondary"
                size="sm"
                (clicked)="addStepOfType('instruction')"
              >
                <fa-icon [icon]="faBook"></fa-icon>
                <span class="hidden md:inline">Instruction</span>
              </ui-button>
              <ui-button
                variant="secondary"
                size="sm"
                (clicked)="addStepOfType('cook')"
              >
                <fa-icon [icon]="faFire"></fa-icon>
                <span class="hidden md:inline">Cook</span>
              </ui-button>
              <ui-button
                variant="secondary"
                size="sm"
                (clicked)="addStepOfType('wait')"
              >
                <fa-icon [icon]="faClock"></fa-icon>
                <span class="hidden md:inline">Wait</span>
              </ui-button>
              <ui-button
                variant="secondary"
                size="sm"
                (clicked)="addStepOfType('serve')"
              >
                <fa-icon [icon]="faUtensils"></fa-icon>
                <span class="hidden md:inline">Serve</span>
              </ui-button>
            </div>
          </div>
        </div>
      </div>

      <!-- Sharing Section -->
      <ui-card [shadow]="false" [bordered]="false" class="mb-4">
        <div class="p-4">
          <h5 class="text-lg font-semibold mb-3">Sharing</h5>

          <ui-checkbox
            [checked]="isPublic()"
            label="Make public"
            (checkedChange)="onIsPublicChange($event)"
          ></ui-checkbox>

          @if (isPublic()) {
            <ui-alert variant="warning" className="mb-0 mt-2">
              <small><strong>All Pantry users</strong> can view this recipe. Only members of your household can edit it.</small>
            </ui-alert>
          } @else {
            <ui-alert variant="info" className="mb-0 mt-2">
              <small>Everyone in your household can view and edit this recipe.</small>
            </ui-alert>
          }
        </div>
      </ui-card>

      <!-- Images are managed from the recipe detail page (tap the hero image),
           mirroring the product detail and iOS handling. -->

      <!-- Classification Facets (manual override) -->
      <ui-card [shadow]="false" [bordered]="false" class="mb-4">
        <div class="p-4">
          <h5 class="text-lg font-semibold mb-3">Classification</h5>

          <app-recipe-facet-editor
            [catalogue]="facetCatalogue()"
            [course]="selectedCourse()"
            [seasons]="selectedSeasons()"
            [dishTypes]="selectedDishTypes()"
            [userEdited]="facetsUserEdited()"
            (courseChange)="onCourseChange($event)"
            (seasonToggle)="toggleSeason($event)"
            (dishTypeToggle)="toggleDishType($event)"/>
        </div>
      </ui-card>
    </div>

    <div class="manage-recipe-ingredients-basket" data-sticky-debug="ingredients">
      <ui-card [shadow]="false" [bordered]="false">
        <div class="p-4">
          <div class="flex justify-between items-center mb-3">
            <h5 class="text-lg font-semibold mb-0">Ingredients</h5>
            <ui-button
              variant="primary"
              [outline]="true"
              size="sm"
              (clicked)="previewIngredients()"
            >
              <fa-icon [icon]="faSync"></fa-icon>
            </ui-button>
          </div>

          @if (aggregatedIngredients().length > 0) {
            <ul class="divide-y divide-base-300">
              @for (ingredient of aggregatedIngredients(); track ingredient.name) {
                <li class="py-3 first:pt-0">
                  <div class="flex justify-between items-center">
                    <div class="flex items-center gap-2 grow min-w-0">
                      <span class="ingredient-name">{{ ingredient.name }}</span>
                    </div>

                    @if (ingredient.quantity?.readable) {
                      <ui-badge variant="primary" class="ml-2">
                        {{ ingredient.quantity!.readable }}
                      </ui-badge>
                    } @else {
                      <ui-badge variant="error" class="ml-2">?</ui-badge>
                    }
                  </div>
                </li>
              }
            </ul>

            <div class="mt-3 text-base-content/60 text-sm text-center">
              <strong>{{ validIngredientsCount }}</strong> ingredient(s) found
            </div>
          } @else {
            <div class="text-base-content/60">No ingredients available.</div>
          }
        </div>
      </ui-card>

    </div>
  </div>
</div>
